import { useState, useEffect } from 'react';
import { getBudget, getMortgage } from '../api';
import { styles, getProgressColor } from '../styles';
import { PlusIcon, Spinner } from '../components/Icons';
import NotificationBell from '../components/NotificationBell';
import InsightCard from '../components/InsightCard';


const USERS = [
  { id: null, name: 'All' },
  { id: '00000000-0000-0000-0000-000000000001', name: 'LCG' },
  { id: '00000000-0000-0000-0000-000000000003', name: 'SGW' },
];

function SettingsIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M2 10h20" />
      <path d="M6 16h4" />
      <circle cx="18" cy="14" r="2" />
    </svg>
  );
}

function HistoryIcon() {
  return (
    <svg width="22" height="22" viewBox="0 -5 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 3v5h5" />
      <path d="M3 8a9 9 0 1 1 2.5 6.3" />
      <path d="M12 7v5l3 3" />
    </svg>
  );
}

function ReceiptsIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="10" cy="10" r="7" />
      <path d="M21 21l-4-4" />
      <path d="M8 8h4M8 11h2" />
    </svg>
  );
}

function ChevronLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15 18l-6-6 6-6"/>
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 18l6-6-6-6"/>
    </svg>
  );
}

function formatMonthDisplay(year, month) {
  const date = new Date(year, month - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export default function DashboardScreen({ currentUser, onAdd, onSettings, onCategorySelect, onMonthChange, onHistory, onReceipts, onNotifications, onMortgage }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [filterUser, setFilterUser] = useState(null); // null = All
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mortgageData, setMortgageData] = useState(null);

  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;

  useEffect(() => {
    setLoading(true);
    getBudget(year, month, filterUser)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [year, month, filterUser]);

  useEffect(() => {
    getMortgage().then(setMortgageData).catch(() => {});
  }, []);

  useEffect(() => {
    if (onMonthChange) {
      onMonthChange({ year, month });
    }
  }, [year, month, onMonthChange]);

  const goToPrevMonth = () => {
    if (month === 1) {
      setYear(y => y - 1);
      setMonth(12);
    } else {
      setMonth(m => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (month === 12) {
      setYear(y => y + 1);
      setMonth(1);
    } else {
      setMonth(m => m + 1);
    }
  };

  if (loading) {
    return (
      <div style={{...styles.screen, ...styles.centered}}>
        <Spinner />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{...styles.screen, ...styles.centered}}>
        <p style={styles.emptyText}>Could not load budget data</p>
      </div>
    );
  }

  const percentUsed = data.total_budget > 0 
    ? (data.total_spent / data.total_budget) * 100 
    : 0;

  return (
    <div style={styles.screen}>
      <header style={styles.dashboardHeader}>
        <h1 style={styles.dashboardTitle}>Budget</h1>
        <div style={dashStyles.headerButtons}>
          <NotificationBell onClick={onNotifications} />
          <button style={dashStyles.settingsBtn} onClick={onReceipts}>
            <ReceiptsIcon />
          </button>
          <button style={dashStyles.settingsBtn} onClick={onHistory}>
            <HistoryIcon />
          </button>
          <button style={dashStyles.settingsBtn} onClick={onSettings}>
            <SettingsIcon />
          </button>
          <button style={styles.addBtn} onClick={onAdd}>
            <PlusIcon />
          </button>
        </div>
      </header>

      <div style={dashStyles.monthNav}>
        <button style={dashStyles.monthBtn} onClick={goToPrevMonth}>
          <ChevronLeft />
        </button>
        <span style={dashStyles.monthLabel}>{formatMonthDisplay(year, month)}</span>
        <button 
          style={{
            ...dashStyles.monthBtn, 
            opacity: isCurrentMonth ? 0.3 : 1,
            cursor: isCurrentMonth ? 'default' : 'pointer'
          }} 
          onClick={goToNextMonth}
        >
          <ChevronRight />
        </button>
      </div>

      {/* User filter toggle */}
      <div style={dashStyles.filterRow}>
        {USERS.map(u => (
          <button
            key={u.id || 'all'}
            style={{
              ...dashStyles.filterBtn,
              ...(filterUser === u.id ? dashStyles.filterBtnActive : {}),
            }}
            onClick={() => setFilterUser(u.id)}
          >
            {u.name}
          </button>
        ))}
      </div>
      
      <div style={styles.summaryCard}>
        <div style={styles.summaryMain}>
          <span style={styles.summarySpent}>${Number(data.total_spent).toFixed(2)}</span>
          <span style={styles.summaryBudget}> / ${Number(data.total_budget).toFixed(2)}</span>
        </div>
        <div style={styles.summaryRemaining}>
          ${Math.abs(Number(data.total_remaining)).toFixed(2)} {data.total_remaining >= 0 ? 'remaining' : 'over budget'}
        </div>
        {Number(data.total_reimbursable) > 0 && (
          <div style={dashStyles.reimbursableRow}>
            + ${Number(data.total_reimbursable).toFixed(2)} reimbursable (not counted)
          </div>
        )}
        <div style={styles.progressTrack}>
          <div
            style={{
              ...styles.progressFill,
              width: `${Math.min(100, percentUsed)}%`,
              backgroundColor: getProgressColor(percentUsed)
            }}
          />
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        <InsightCard onGenerated={onNotifications} />
      </div>

      {onMortgage && (
        <button style={dashStyles.mortgageCard} onClick={onMortgage}>
          <div style={dashStyles.mortgageLeft}>
            <span style={dashStyles.mortgageIcon}>🏠</span>
            <div style={dashStyles.mortgageInfo}>
              {mortgageData ? (
                <>
                  <span style={dashStyles.mortgageName}>{mortgageData.mortgage.name}</span>
                  <span style={dashStyles.mortgageBalance}>
                    ${Number(mortgageData.current_balance).toLocaleString('en-US', { maximumFractionDigits: 0 })} remaining
                  </span>
                </>
              ) : (
                <>
                  <span style={dashStyles.mortgageName}>Mortgage</span>
                  <span style={dashStyles.mortgageBalance}>Tap to set up</span>
                </>
              )}
            </div>
          </div>
          <div style={dashStyles.mortgageRight}>
            {mortgageData && (
              <span style={dashStyles.mortgagePayment}>
                ${Number(mortgageData.next_scheduled_payment).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo
              </span>
            )}
            <span style={dashStyles.mortgageChevron}>›</span>
          </div>
        </button>
      )}

      {data.by_category.length > 0 && (
        <section style={{ ...styles.section, marginTop: 10 }}>
          <h2 style={styles.sectionTitle}>Categories</h2>
          <div style={dashStyles.categoryScroll}>
            {data.by_category.map(cat => {
              const pct = cat.monthly_limit > 0 ? cat.percent_used : 0;
              const ytdPct = cat.ytd_budget > 0 ? cat.ytd_percent_used : 0;
              const hasYtd = cat.ytd_budget && cat.ytd_budget > 0;
              const expectedPct = cat.ytd_budget > 0 
                ? (Number(cat.ytd_expected) / Number(cat.ytd_budget)) * 100 
                : 0;
              
              return (
                <button 
                  key={cat.category.id} 
                  style={dashStyles.categoryButton}
                  onClick={() => onCategorySelect(cat.category, cat, { year, month })}
                >
                  <div style={styles.categoryTop}>
                    <span style={styles.categoryName}>{cat.category.name}</span>
                    <span style={styles.categoryAmount}>
                      ${Number(cat.spent_this_month).toFixed(0)}
                      <span style={styles.categoryLimit}> / ${Number(cat.monthly_limit).toFixed(0)}</span>
                    </span>
                  </div>
                  <div style={styles.progressTrackSmall}>
                    <div 
                      style={{
                        ...styles.progressFill,
                        width: `${Math.min(100, pct)}%`,
                        backgroundColor: getProgressColor(pct)
                      }} 
                    />
                  </div>
                  
                  {/* YTD bar with expected tick */}
                  {hasYtd && (
                    <div style={dashStyles.ytdRow}>
                      <span style={dashStyles.ytdLabel}>YTD</span>
                      <div style={dashStyles.ytdTrackWrapper}>
                        <div style={dashStyles.ytdTrack}>
                          <div 
                            style={{
                              ...styles.progressFill,
                              width: `${Math.min(100, ytdPct)}%`,
                              backgroundColor: Number(cat.ytd_spent) > Number(cat.ytd_expected) 
                                ? '#ef4444' 
                                : getProgressColor(ytdPct),
                              opacity: 0.7,
                            }}  
                          />
                        </div>
                        {/* Expected tick marker */}
                        <div 
                          style={{
                            ...dashStyles.expectedTick,
                            left: `${Math.min(100, expectedPct)}%`,
                          }}
                          title={`Expected: $${Number(cat.ytd_expected).toFixed(0)}`}
                        />
                      </div>
                      <span style={dashStyles.ytdAmount}>
                        ${Number(cat.ytd_spent).toFixed(0)} / ${Number(cat.ytd_budget).toFixed(0)}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {data.project_categories && data.project_categories.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Projects</h2>
          <div style={styles.categoryList}>
            {data.project_categories.map(proj => {
              const pct = proj.total_limit && proj.total_limit > 0 ? proj.percent_used : 0;
              return (
                <button
                  key={proj.category.id}
                  style={dashStyles.categoryButton}
                  onClick={() => onCategorySelect(proj.category, {
                    spent_this_month: proj.spent_total,
                    monthly_limit: proj.total_limit || 0,
                    remaining: proj.remaining || 0,
                    percent_used: proj.percent_used,
                  }, { year, month })}
                >
                  <div style={styles.categoryTop}>
                    <span style={styles.categoryName}>{proj.category.icon} {proj.category.name}</span>
                    <span style={styles.categoryAmount}>
                      ${Number(proj.spent_total).toFixed(0)}
                      {proj.total_limit && (
                        <span style={styles.categoryLimit}> / ${Number(proj.total_limit).toFixed(0)}</span>
                      )}
                    </span>
                  </div>
                  {proj.total_limit && (
                    <div style={styles.progressTrackSmall}>
                      <div
                        style={{
                          ...styles.progressFill,
                          width: `${Math.min(100, pct)}%`,
                          backgroundColor: getProgressColor(pct),
                        }}
                      />
                    </div>
                  )}
                  <div style={dashStyles.envelopeLabel}>Total envelope</div>
                  {proj.sub_categories.length > 0 && (
                    <div style={dashStyles.subCatList}>
                      {proj.sub_categories.map(sub => (
                        <div key={sub.category.id} style={dashStyles.subCatRow}>
                          <span style={dashStyles.subCatName}>{sub.category.icon} {sub.category.name}</span>
                          <span style={dashStyles.subCatAmount}>
                            ${Number(sub.spent_total).toFixed(0)}
                            {sub.budget_limit && (
                              <span style={{ color: '#94a3b8' }}> / ${Number(sub.budget_limit).toFixed(0)}</span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {data.recent_receipts.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Recent</h2>
          <div style={styles.recentList}>
            {data.recent_receipts.slice(0, 5).map(r => (
              <div key={r.id} style={styles.recentItem}>
                <div style={styles.recentLeft}>
                  <span style={styles.recentMerchant}>
                    {r.merchant_name || 'Unknown'}
                    {r.user_name && <span style={dashStyles.userName}> · {r.user_name}</span>}
                    {r.expense_type === 'corporate' && (
                      <span style={dashStyles.corpBadge}>CORP</span>
                    )}
                  </span>
                  <span style={styles.recentCategory}>{r.category?.name || 'Uncategorized'}</span>
                </div>
                <span style={styles.recentAmount}>${Number(r.grand_total).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const dashStyles = {
  headerButtons: {
    display: 'flex',
    gap: 5,
  },
  settingsBtn: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: 'white',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    overflow: 'visible',
  },
  categoryButton: {
    width: '100%',
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 14,
    border: '1px solid #e2e8f0',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.15s',
  },
  monthNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    marginBottom: 12,
  },
  monthBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#64748b',
  },
  monthLabel: {
    fontSize: 16,
    fontWeight: 600,
    color: '#1e293b',
    minWidth: 160,
    textAlign: 'center',
  },
  filterRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 20,
    justifyContent: 'center',
  },
  filterBtn: {
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    backgroundColor: 'white',
    color: '#64748b',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
  filterBtnActive: {
    backgroundColor: '#6366f1',
    borderColor: '#6366f1',
    color: 'white',
  },
  userName: {
    color: '#94a3b8',
    fontWeight: 400,
  },
  ytdRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  ytdLabel: {
    fontSize: 10,
    color: '#94a3b8',
    fontWeight: 500,
    width: 26,
  },
  ytdTrackWrapper: {
    flex: 1,
    position: 'relative',
  },
  ytdTrack: {
    height: 4,
    backgroundColor: '#e2e8f0',
    borderRadius: 2,
    overflow: 'hidden',
  },
  expectedTick: {
    position: 'absolute',
    top: -2,
    width: 2,
    height: 8,
    backgroundColor: '#1e293b',
    borderRadius: 1,
    transform: 'translateX(-50%)',
  },
  ytdAmount: {
    fontSize: 10,
    color: '#94a3b8',
    minWidth: 90,
    textAlign: 'right',
  },
  reimbursableRow: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 4,
    marginBottom: 4,
  },
  envelopeLabel: {
    fontSize: 10,
    color: '#94a3b8',
    fontWeight: 500,
    marginTop: 4,
  },
  subCatList: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px solid #f1f5f9',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  subCatRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  subCatName: {
    fontSize: 13,
    color: '#475569',
  },
  subCatAmount: {
    fontSize: 13,
    color: '#475569',
    fontWeight: 500,
  },
  corpBadge: {
    fontSize: 9,
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    padding: '1px 5px',
    borderRadius: 4,
    fontWeight: 600,
    marginLeft: 5,
  },
  mortgageCard: {
    width: '100%',
    marginTop: 10,
    backgroundColor: 'white',
    borderRadius: 12,
    padding: '12px 16px',
    border: '1px solid #e2e8f0',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: 'left',
    boxSizing: 'border-box',
  },
  mortgageLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  mortgageIcon: {
    fontSize: 22,
  },
  mortgageInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  mortgageName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1e293b',
  },
  mortgageBalance: {
    fontSize: 12,
    color: '#64748b',
  },
  mortgageRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  mortgagePayment: {
    fontSize: 14,
    fontWeight: 600,
    color: '#6366f1',
  },
  mortgageChevron: {
    fontSize: 18,
    color: '#94a3b8',
    lineHeight: 1,
  },
  categoryScroll: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    maxHeight: 320,
    overflowY: 'auto',
    paddingRight: 2,
  },
};